import { useCallback } from "react";
import {
  keepPreviousData,
  useQuery,
  useQueryClient,
} from "@tanstack/react-query";
import {
  useBKGIndexCompanyFilter,
  useBKGNameOptions,
  useBKGSearch,
  useBKGVCFilter,
  useBKGVCIndex,
} from ".";

// React Query aborts a query's signal when it is superseded or its view
// unmounts, so every fetcher takes one: a search left behind by the next
// keystroke stops instead of running to completion on the server.

export const bkgSearchKeys = {
  all: ["bkg"] as const,
  companyFilter: (workspaceId: string) =>
    [...bkgSearchKeys.all, "companyFilter", workspaceId] as const,
  companySearch: (workspaceId: string, query: unknown) =>
    [...bkgSearchKeys.all, "companySearch", workspaceId, query] as const,
  vcFilter: (workspaceId: string) =>
    [...bkgSearchKeys.all, "vcFilter", workspaceId] as const,
  vcSearch: (workspaceId: string, query: unknown) =>
    [...bkgSearchKeys.all, "vcSearch", workspaceId, query] as const,
  nameOptions: (workspaceId: string, index: string, query: string) =>
    [...bkgSearchKeys.all, "nameOptions", workspaceId, index, query] as const,
};

// The filter schema only changes when the index is rebuilt.
const SCHEMA_STALE_TIME = 5 * 60 * 1000;

export type CompanySearchParams = {
  skip: number;
  limit: number;
  company_filter?: unknown;
};

export type VCSearchParams = {
  skip: number;
  limit: number;
  venture_capital_filter?: Record<string, any>;
};

export function useCompanyFilterSchemaQuery(
  workspaceId: string,
  enabled = true,
) {
  const fetchSchema = useBKGIndexCompanyFilter();
  return useQuery({
    queryKey: bkgSearchKeys.companyFilter(workspaceId),
    queryFn: ({ signal }) => fetchSchema(workspaceId, signal),
    enabled: enabled && !!workspaceId,
    staleTime: SCHEMA_STALE_TIME,
  });
}

// Each page carries the query it answers: with keepPreviousData the page on
// screen can belong to an older query than the one being fetched.
export function useCompanySearchQuery(
  workspaceId: string,
  query: CompanySearchParams,
  enabled = true,
) {
  const search = useBKGSearch();
  return useQuery({
    queryKey: bkgSearchKeys.companySearch(workspaceId, query),
    queryFn: async ({ signal }) => ({
      query,
      page: await search(workspaceId, query, signal),
    }),
    enabled: enabled && !!workspaceId,
    placeholderData: keepPreviousData,
  });
}

export function useVCFilterSchemaQuery(workspaceId: string, enabled = true) {
  const fetchSchema = useBKGVCFilter();
  return useQuery({
    queryKey: bkgSearchKeys.vcFilter(workspaceId),
    queryFn: ({ signal }) => fetchSchema(workspaceId, signal),
    enabled: enabled && !!workspaceId,
    staleTime: SCHEMA_STALE_TIME,
  });
}

export function useVCSearchQuery(
  workspaceId: string,
  query: VCSearchParams,
  enabled = true,
) {
  const index = useBKGVCIndex();
  return useQuery({
    queryKey: bkgSearchKeys.vcSearch(workspaceId, query),
    queryFn: async ({ signal }) => {
      const filter = query.venture_capital_filter;
      const page = await index(workspaceId, {
        limit: query.limit,
        offset: query.skip,
        orderBy: "name",
        orderDir: "asc",
        filter: filter && Object.keys(filter).length ? filter : undefined,
        signal,
      });
      return { query, page };
    },
    enabled: enabled && !!workspaceId,
    placeholderData: keepPreviousData,
  });
}

// The names matching what was typed into a name filter. The previous matches
// stay listed while the next ones load, so the list doesn't flash empty.
export function useNameOptionsQuery(
  workspaceId: string,
  index: string,
  query: string,
  enabled = true,
) {
  const fetchNames = useBKGNameOptions();
  return useQuery({
    queryKey: bkgSearchKeys.nameOptions(workspaceId, index, query),
    queryFn: ({ signal }) => fetchNames(workspaceId, index, query, signal),
    enabled: enabled && !!workspaceId,
    staleTime: SCHEMA_STALE_TIME,
    placeholderData: keepPreviousData,
  });
}

// Looks one name up outside a render, for resolving a pasted list of names.
// Shares the typeahead's cache.
export function useNameLookup(workspaceId: string, index: string) {
  const fetchNames = useBKGNameOptions();
  const queryClient = useQueryClient();
  return useCallback(
    (query: string) =>
      queryClient.fetchQuery({
        queryKey: bkgSearchKeys.nameOptions(workspaceId, index, query),
        queryFn: ({ signal }) => fetchNames(workspaceId, index, query, signal),
        staleTime: SCHEMA_STALE_TIME,
      }),
    [fetchNames, queryClient, workspaceId, index],
  );
}
